<script setup>
import { ref, computed, onMounted } from 'vue';
import controleAcessoAPI from 'dashboard/api/controleAcesso';
import { useAlert } from 'dashboard/composables';
import {
  ACCESS_CONTROL_CATALOG,
  flattenCatalogKeys,
} from 'dashboard/constants/accessControl.js';

const ROLE_LABELS = {
  administrator: 'Administradores',
  supervisor: 'Supervisores',
  operacional: 'Operacional',
  agent: 'Agentes',
  // v101: 3 perfis novos (veem todas as conversas das caixas onde sao membros)
  qualidade: 'Qualidade',
  ouvidoria: 'Ouvidoria',
  juridico: 'Jurídico',
};
const BASE_ROLES = [
  'administrator',
  'supervisor',
  'operacional',
  'agent',
  'qualidade',
  'ouvidoria',
  'juridico',
];

const catalog = ACCESS_CONTROL_CATALOG;

const loading = ref(false);
const saving = ref(false);
const usuarios = ref([]);
const customRoles = ref([]);
const selectedProfile = ref('administrator'); // role string ou `custom:<id>`
const selectedUserId = ref(null);
const blockedSet = ref(new Set());

// CAIXAS VISIVEIS (T4/A7): visibilidade de caixa e InboxMember (mecanismo
// nativo, ja respeitado em toda a app -- inboxes_controller, InboxPolicy,
// conversas). `inboxes` = caixas da conta (vem do backend, id+name);
// `inboxSet` = as caixas marcadas para o USUARIO selecionado no momento.
const inboxes = ref([]);
const inboxSet = ref(new Set());

const profiles = computed(() => {
  const list = BASE_ROLES.map(role => ({ key: role, label: ROLE_LABELS[role] }));
  customRoles.value.forEach(cr =>
    list.push({ key: `custom:${cr.id}`, label: cr.name })
  );
  return list;
});

// v112 (A4): antes, quem tinha custom_role_id era EXCLUIDO da listagem do
// perfil-base (`!user.custom_role_id`) -- nao aparecia em lugar NENHUM da
// tela (nem no perfil base, nem no filtro `custom:<id>`, ja que o perfil
// personalizado dele podia nem estar selecionado), entao nunca entrava no
// save em lote "TODOS" e as regras do Controle de Acesso simplesmente nao
// valiam pra ele. Agora aparece tanto no perfil-base (`user.role`) quanto no
// filtro do custom role dele -- os dois "TODOS" o alcancam.
const usersOfProfile = computed(() =>
  usuarios.value.filter(user => {
    if (selectedProfile.value.startsWith('custom:')) {
      const id = Number(selectedProfile.value.split(':')[1]);
      return user.custom_role_id === id;
    }
    return user.role === selectedProfile.value;
  })
);

const ALL_SENTINEL = '__ALL__';
const isAllSelected = computed(() => selectedUserId.value === ALL_SENTINEL);
const currentProfileLabel = computed(
  () => (profiles.value.find(p => p.key === selectedProfile.value) || {}).label || ''
);

const selectedUser = computed(
  () => usuarios.value.find(user => user.id === selectedUserId.value) || null
);

const isAllowed = key => !blockedSet.value.has(key);

const allKeysOf = node => {
  const keys = [node.key];
  (node.children || []).forEach(child => keys.push(...allKeysOf(child)));
  return keys;
};

// Marcar/desmarcar um no. Ao desmarcar, bloqueia o no e TODOS os filhos (cascata)
// para o guard de rota tambem bloquear as sub-rotas. Ao marcar, libera tudo.
const toggleKey = node => {
  const keys = allKeysOf(node);
  const next = new Set(blockedSet.value);
  if (isAllowed(node.key)) {
    keys.forEach(key => next.add(key));
  } else {
    keys.forEach(key => next.delete(key));
  }
  blockedSet.value = next;
};

const setSection = (section, allow) => {
  const keys = allKeysOf(section);
  const next = new Set(blockedSet.value);
  keys.forEach(key => (allow ? next.delete(key) : next.add(key)));
  blockedSet.value = next;
};

const selectUser = user => {
  selectedUserId.value = user.id;
  blockedSet.value = new Set(user.blocked || []);
  inboxSet.value = new Set(user.inbox_ids || []);
};

// T4/A7: marca/desmarca uma caixa para o usuario selecionado. So chamado
// quando NAO e administrador (a tela mostra texto no lugar dos checkboxes
// pra admin -- ver template).
const isInboxAllowed = inboxId => inboxSet.value.has(inboxId);
const toggleInbox = inboxId => {
  const next = new Set(inboxSet.value);
  if (next.has(inboxId)) {
    next.delete(inboxId);
  } else {
    next.add(inboxId);
  }
  inboxSet.value = next;
};

// Card "TODOS": configura em lote para todos os usuarios do perfil. Comeca com
// tudo liberado; ao salvar, sobrescreve a config de todos do perfil (menos id 1).
// PADRONIZAR (v85): padroes por perfil vindos do backend; checkbox do card
// TODOS. Marcado ao aplicar -> vira padrao do perfil (usuario NOVO daquele
// perfil ja nasce com esses bloqueios). Desmarcado ao aplicar -> remove.
const padroes = ref({});
const padronizar = ref(false);

// FUNCOES RESTRITAS (v87): WhatsApp ao vivo, menu Meta e Horario de Uso —
// bloqueadas por padrao; id 1 marca AQUI quem pode usar cada uma.
const FUNCOES_RESTRITAS = [
  { key: 'live_whatsapp', label: 'WhatsApp ao vivo (Campanhas)' },
  { key: 'meta_menu', label: 'Menu Meta (Template / Disparo / Histórico)' },
  { key: 'horario_uso', label: 'Horário de Uso' },
  { key: 'privilegios', label: 'Privilégios das caixas (aba Privilégios)' },
];
const restritaSel = ref({ live_whatsapp: {}, meta_menu: {}, horario_uso: {}, privilegios: {} });
const salvandoRestrita = ref('');

const carregarRestritas = mapa => {
  FUNCOES_RESTRITAS.forEach(f => {
    const marcados = {};
    (mapa?.[f.key] || []).forEach(id => {
      marcados[id] = true;
    });
    restritaSel.value[f.key] = marcados;
  });
};

const salvarRestrita = async funcao => {
  salvandoRestrita.value = funcao;
  try {
    const ids = Object.keys(restritaSel.value[funcao])
      .filter(id => restritaSel.value[funcao][id])
      .map(Number);
    await controleAcessoAPI.salvarFuncaoRestrita(funcao, ids);
    useAlert('Função restrita salva — vale no próximo acesso de cada usuário.');
  } catch (error) {
    useAlert('Erro ao salvar função restrita.');
  } finally {
    salvandoRestrita.value = '';
  }
};

// v112 (A1): "TODOS deste perfil" comecava sempre com o blockedSet ZERADO
// (tudo liberado) -- clicar em TODOS e Salvar sem tocar em nada apagava a
// blocklist inteira do perfil, mesmo que ja existisse um padrao configurado.
// Agora pre-carrega com o PADRAO salvo do perfil (CONTROLE_ACESSO_PADROES,
// vindo do backend em `padroes`), igual ao que `selectUser` ja faz para um
// usuario individual (`user.blocked`). Perfil sem padrao ainda comeca vazio
// (nunca foi padronizado -- nao ha o que pre-carregar).
const selectAll = () => {
  selectedUserId.value = ALL_SENTINEL;
  blockedSet.value = new Set(padroes.value[selectedProfile.value] || []);
  // M3 (revisao final): `!!padroes.value[...]` marcava PADRONIZAR mesmo para
  // um padrao vazio-mas-truthy (ex.: `[]`, que em JS e truthy) -- um perfil
  // padronizado com blocklist vazia (todo mundo libera) reabria a tela com o
  // checkbox desmarcado, escondendo que ele ESTAVA padronizado. O sinal
  // correto de "este perfil tem padrao salvo" e a CHAVE existir no mapa como
  // array (mesmo vazio), nao o valor ser truthy.
  padronizar.value = Array.isArray(padroes.value[selectedProfile.value]);
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await controleAcessoAPI.fetch();
    usuarios.value = data.usuarios || [];
    customRoles.value = data.custom_roles || [];
    padroes.value = data.padroes || {};
    inboxes.value = data.inboxes || [];
    carregarRestritas(data.funcoes_restritas);
  } catch (error) {
    useAlert('Erro ao carregar usuários.');
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  if (!isAllSelected.value && !selectedUser.value) return;
  const blocklist = Array.from(blockedSet.value);
  // v112 (A3): manda TODAS as keys do catalogo atual junto do save. O backend
  // grava em ui_known_keys — assim getBlockedFeatureKeys sabe distinguir
  // "usuario liberou essa feature de proposito" de "essa feature nem existia
  // quando o usuario foi configurado pela ultima vez" (a segunda nasce
  // bloqueada para nao-admin, evitando o gap G3 do diagnostico).
  const knownKeys = flattenCatalogKeys();

  // Lote (card TODOS): aplica a mesma config a todos do perfil, sobrescrevendo
  // ajustes individuais. Confirma antes (acao destrutiva).
  if (isAllSelected.value) {
    const ids = usersOfProfile.value.map(user => user.id);
    if (!ids.length) {
      useAlert('Nenhum usuário neste perfil.');
      return;
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Aplicar esta configuração a TODOS os ${ids.length} usuário(s) de "${currentProfileLabel.value}"? Isso substitui ajustes individuais. (O super admin id 1 não é afetado.)`
    );
    if (!ok) return;
    saving.value = true;
    try {
      const { data } = await controleAcessoAPI.save({
        user_ids: ids,
        blocklist,
        known_keys: knownKeys,
        padronizar: padronizar.value,
        profile_key: selectedProfile.value,
      });
      usuarios.value.forEach(user => {
        // M4 (revisao final): copia ([...blocklist]), nao a MESMA referencia
        // do array -- evita que todos os usuarios do lote acabem apontando
        // pro mesmo array `blocklist` (qualquer mutacao futura in-place --
        // .push/.splice -- num usuario vazaria pros outros do lote).
        if (ids.includes(user.id)) user.blocked = [...blocklist];
      });
      padroes.value = data.padroes || padroes.value;
      useAlert(
        padronizar.value
          ? 'Aplicado a todos e PADRONIZADO: usuário novo deste perfil já nasce com esses bloqueios.'
          : 'Aplicado a todos do perfil. Vale no próximo acesso de cada um.'
      );
    } catch (error) {
      useAlert('Erro ao salvar.');
    } finally {
      saving.value = false;
    }
    return;
  }

  // Individual. T4/A7: manda inbox_ids junto (mesma acao/mesmo botao) --
  // o backend so aplica a sincronizacao de InboxMember nesse caminho
  // (nunca no lote "TODOS", ver controle_acesso_controller.rb#update).
  saving.value = true;
  try {
    const { data } = await controleAcessoAPI.save({
      user_id: selectedUser.value.id,
      blocklist,
      known_keys: knownKeys,
      inbox_ids: Array.from(inboxSet.value),
    });
    const stored = usuarios.value.find(user => user.id === selectedUser.value.id);
    if (stored) {
      stored.blocked = blocklist;
      stored.inbox_ids = data.inbox_ids || Array.from(inboxSet.value);
    }
    useAlert('Permissões salvas. Valem no próximo acesso do usuário.');
  } catch (error) {
    useAlert('Erro ao salvar.');
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-3 p-4 lg:p-6 h-full w-full overflow-auto bg-n-background text-n-slate-12">
    <header class="flex flex-col gap-0.5">
      <h1 class="text-xl font-semibold">Controle de Acesso</h1>
      <p class="text-sm text-n-slate-11 max-w-3xl">
        Defina o que cada usuário enxerga no sistema. Verde = tem acesso; ao
        desmarcar, a opção some da tela dele. Vale no próximo acesso do usuário.
        O super admin (id 1) não aparece e nunca é afetado.
      </p>
    </header>

    <div v-if="loading" class="text-n-slate-11">Carregando…</div>

    <template v-else>
      <!-- Seletor de perfil -->
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="profile in profiles"
          :key="profile.key"
          class="px-3 py-1 text-sm rounded-lg border border-n-weak"
          :class="
            selectedProfile === profile.key
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="
            selectedProfile = profile.key;
            selectedUserId = null;
          "
        >
          {{ profile.label }}
        </button>
      </div>

      <div class="flex flex-col gap-4 lg:flex-row">
        <!-- Lista de usuarios do perfil -->
        <div class="shrink-0 p-2 w-full lg:w-64 rounded-lg border border-n-weak">
          <!-- Card TODOS: aplica em lote a todos do perfil -->
          <button
            class="px-2 py-2 mb-2 w-full text-left rounded-lg border border-dashed border-n-weak"
            :class="
              isAllSelected
                ? 'bg-n-brand/10 outline outline-1 outline-n-brand'
                : 'hover:bg-n-alpha-1'
            "
            @click="selectAll"
          >
            <div class="flex gap-2 items-center font-semibold">
              <span class="i-lucide-users size-4" />
              <span>TODOS deste perfil</span>
            </div>
            <div class="text-xs text-n-slate-11">
              Aplica a {{ usersOfProfile.length }} usuário(s) de uma vez
            </div>
          </button>
          <p
            v-if="!usersOfProfile.length"
            class="p-2 text-sm text-n-slate-11"
          >
            Nenhum usuário neste perfil.
          </p>
          <button
            v-for="user in usersOfProfile"
            :key="user.id"
            class="px-2 py-2 mb-1 w-full text-left rounded-lg"
            :class="
              selectedUserId === user.id
                ? 'bg-n-brand/10 outline outline-1 outline-n-brand'
                : 'hover:bg-n-alpha-1'
            "
            @click="selectUser(user)"
          >
            <div class="flex gap-1 items-center">
              <span class="font-medium truncate">{{ user.name }}</span>
              <!-- A4: usuario com funcao personalizada aparece tambem no
                   perfil-base dele (role nativo); o badge deixa claro pra
                   quem esta configurando que esse usuario tem um custom role
                   por cima do papel nativo. -->
              <span
                v-if="user.custom_role_id"
                class="px-1.5 py-0.5 text-[10px] rounded-full shrink-0 bg-n-slate-4 text-n-slate-11"
                :title="user.custom_role_name || 'Função personalizada'"
              >
                função personalizada
              </span>
            </div>
            <div class="text-xs truncate text-n-slate-11">{{ user.email }}</div>
          </button>
        </div>

        <!-- Arvore de funcoes -->
        <div class="flex-1 min-w-0 p-3 rounded-lg border border-n-weak">
          <div v-if="!selectedUser && !isAllSelected" class="p-2 text-n-slate-11">
            Selecione um usuário (ou "TODOS deste perfil") para configurar o acesso.
          </div>
          <div v-else class="flex flex-col gap-3">
            <div
              v-if="isAllSelected"
              class="p-2.5 text-sm rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-800"
            >
              Configurando <strong>TODOS</strong> os {{ usersOfProfile.length }}
              usuário(s) de "{{ currentProfileLabel }}". Ao salvar, esta configuração
              <strong>substitui</strong> os ajustes individuais (o super admin id 1
              não é afetado).
            </div>
            <!-- PADRONIZAR (v85): usuario novo do perfil nasce com esses bloqueios -->
            <label
              v-if="isAllSelected"
              class="flex gap-2 items-start p-2.5 text-sm rounded-lg border border-n-weak cursor-pointer"
            >
              <input v-model="padronizar" type="checkbox" class="mt-0.5" />
              <span>
                <strong>DESEJA PADRONIZAR ESSAS ALTERAÇÕES?</strong><br />
                <span class="text-n-slate-11">
                  Marcado: todo usuário <strong>novo</strong> criado no perfil
                  "{{ currentProfileLabel }}" já nasce com estes bloqueios
                  aplicados automaticamente. Desmarcado: remove o padrão deste
                  perfil (novos usuários nascem sem bloqueio).
                </span>
              </span>
            </label>

            <!-- Arvore de features: grid multi-coluna (auto-fill) para caber
                 varios grupos lado a lado em vez de empilhar verticalmente. -->
            <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2">
              <div
                v-for="section in catalog"
                :key="section.key"
                class="p-2.5 rounded-lg border border-n-weak"
              >
                <div class="flex justify-between items-center mb-1.5">
                  <label class="flex gap-2 items-center font-semibold cursor-pointer">
                    <input
                      type="checkbox"
                      :checked="isAllowed(section.key)"
                      @change="toggleKey(section)"
                    />
                    <span>{{ section.label }}</span>
                  </label>
                  <div v-if="section.children" class="flex gap-2 text-xs">
                    <button class="underline text-n-slate-11" @click="setSection(section, true)">
                      Marcar todos
                    </button>
                    <button class="underline text-n-slate-11" @click="setSection(section, false)">
                      Desmarcar todos
                    </button>
                  </div>
                </div>
                <div
                  v-if="section.children"
                  class="grid grid-cols-1 gap-1.5 pl-4"
                >
                  <div v-for="child in section.children" :key="child.key">
                    <label class="flex gap-2 items-center cursor-pointer">
                      <input
                        type="checkbox"
                        :checked="isAllowed(child.key)"
                        @change="toggleKey(child)"
                      />
                      <span
                        class="inline-block rounded-sm size-3"
                        :class="isAllowed(child.key) ? 'bg-green-500' : 'bg-n-slate-5'"
                      />
                      <span>{{ child.label }}</span>
                    </label>

                    <!-- 3º nível: hoje só "Caixas de Entrada" tem filhos (os tipos
                         de canal). Só aparece com o pai marcado — sem acesso à
                         tela de caixas, escolher canal não significa nada. -->
                    <div
                      v-if="child.children && isAllowed(child.key)"
                      class="grid grid-cols-1 gap-1 py-1.5 pl-4 mt-1 rounded-md bg-n-slate-2"
                    >
                      <p class="col-span-full mb-1 text-xs text-n-slate-11">
                        Tipos de caixa que este usuário pode criar:
                      </p>
                      <label
                        v-for="grand in child.children"
                        :key="grand.key"
                        class="flex gap-2 items-center text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          :checked="isAllowed(grand.key)"
                          @change="toggleKey(grand)"
                        />
                        <span
                          class="inline-block rounded-sm size-2.5"
                          :class="isAllowed(grand.key) ? 'bg-green-500' : 'bg-n-slate-5'"
                        />
                        <span>{{ grand.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- CAIXAS VISIVEIS (T4/A7): so por usuario -- membership
                 (InboxMember) e individual, aplicar em massa via "TODOS"
                 seria perigoso (ver controle_acesso_controller.rb#update).
                 No card TODOS mostramos so um aviso, sem a secao.
                 <details> colapsavel (fechado por padrao) para o foco
                 inicial ficar na arvore de features. -->
            <details v-if="!isAllSelected" class="p-2.5 rounded-lg border border-n-weak">
              <summary class="font-semibold cursor-pointer select-none">
                Caixas visíveis
              </summary>
              <div class="mt-2">
                <p
                  v-if="selectedUser && selectedUser.role === 'administrator'"
                  class="text-sm text-n-slate-11"
                >
                  Administradores veem todas as caixas.
                </p>
                <template v-else>
                  <!-- I2 (revisao final): marcar/desmarcar aqui e InboxMember de
                       verdade (mesmo mecanismo nativo de "Agentes" da caixa),
                       que dispara os callbacks de round-robin
                       (add_agent_to_round_robin/remove_agent_from_round_robin
                       em InboxMember) -- avisa antes de alguem se surpreender
                       com o usuario entrando/saindo da distribuicao automatica
                       so por causa desta tela. -->
                  <p class="mb-2 text-xs text-n-slate-11">
                    Marcar/desmarcar também inclui/remove o usuário da
                    distribuição automática de conversas da caixa.
                  </p>
                  <p v-if="!inboxes.length" class="text-sm text-n-slate-11">
                    Nenhuma caixa nesta conta.
                  </p>
                  <div v-else class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <label
                      v-for="inbox in inboxes"
                      :key="inbox.id"
                      class="flex gap-2 items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        :checked="isInboxAllowed(inbox.id)"
                        @change="toggleInbox(inbox.id)"
                      />
                      <span
                        class="inline-block rounded-sm size-3"
                        :class="isInboxAllowed(inbox.id) ? 'bg-green-500' : 'bg-n-slate-5'"
                      />
                      <span>{{ inbox.name }}</span>
                    </label>
                  </div>
                </template>
              </div>
            </details>
            <p v-else class="text-xs text-n-slate-11">
              Caixas visíveis são configuradas por usuário.
            </p>

            <div class="sticky bottom-0 py-2 bg-n-background">
              <button
                class="px-4 py-2 text-white rounded-lg bg-n-brand disabled:opacity-50"
                :disabled="saving"
                @click="save"
              >
                {{ saving ? 'Salvando…' : 'Salvar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ============ FUNCOES RESTRITAS (v87) ============ -->
    <!-- Colapsavel e fechada por padrao: e a secao mais alta da tela (4
         cards com lista de usuarios cada) e a menos usada no dia a dia --
         manter fechada deixa a arvore de features como foco inicial e
         evita que a pagina fique alta demais por padrao. -->
    <details class="p-3 rounded-lg border border-n-weak">
      <summary class="text-lg font-semibold text-n-slate-12 cursor-pointer select-none">
        Funções restritas — liberar por usuário
      </summary>
      <p class="text-sm text-n-slate-11 mt-1 mb-3">
        Estas funções ficam <strong>bloqueadas para todo mundo</strong> (menos
        você). Marque quem pode usar cada uma e salve. Vale no próximo acesso.
      </p>
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div
          v-for="funcao in FUNCOES_RESTRITAS"
          :key="funcao.key"
          class="p-2.5 rounded-lg border border-n-weak flex flex-col gap-2"
        >
          <p class="font-medium text-n-slate-12">{{ funcao.label }}</p>
          <div class="max-h-48 overflow-y-auto flex flex-col gap-1">
            <label
              v-for="user in usuarios"
              :key="`${funcao.key}-${user.id}`"
              class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
            >
              <input
                v-model="restritaSel[funcao.key][user.id]"
                type="checkbox"
              />
              <span class="truncate">{{ user.name }}</span>
              <span class="text-xs text-n-slate-10 truncate">{{ user.email }}</span>
            </label>
          </div>
          <button
            class="self-end px-4 py-1.5 text-white rounded-lg bg-n-brand disabled:opacity-50 text-sm"
            :disabled="salvandoRestrita === funcao.key"
            @click="salvarRestrita(funcao.key)"
          >
            {{ salvandoRestrita === funcao.key ? 'Salvando…' : 'Salvar' }}
          </button>
        </div>
      </div>
    </details>
  </div>
</template>
